import { router, usePage, usePoll } from '@inertiajs/vue3'
import { useToast } from 'primevue/usetoast'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTransactionStore } from '@/stores/transaction'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEsgStore } from '@/stores/useEsgStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import { useInfraStore } from '@/stores/useInfraStore'
import { DETAIL_ESG } from '../constants'
import { useFormatDate } from '@/composables/useFormatDate'

export function useExportDetail() {
  const toast = useToast()
  const { t } = useI18n()
  const { formatDatePickerToISO } = useFormatDate()
  const detailStore = useDetailStore()
  const evaluationStore = useEvaluationStore()
  const infraStore = useInfraStore()
  const transactionStore = useTransactionStore()
  const esgStore = useEsgStore()

  const internalExportForm = ref({})

  // ── Map capture auto-wait (replaces the old esg_maps_not_loaded abort) ──
  // The 8 offscreen ESG maps and the plannings card capture independently
  // and there is no "ready" event, so an early export waits on the store's
  // derived readiness, nudging blank/failed captures twice before giving up.
  // The ESG side still aborts on timeout (their maps are static and a
  // missing capture means the connection is gone); the plannings side is
  // bounded but never fatal — its overlay rides a rotating external
  // service (RED-293), and a dead upstream must degrade to a map without
  // land-use colours, never to a report the appraiser cannot export. Every
  // watcher and timer is torn down on any exit — nothing leaks past the
  // promise.

  const ESG_WAIT_TIMEOUT_MS = 30000
  const PLANNING_WAIT_TIMEOUT_MS = 20000
  const RETRY_AT_MS = [8000, 18000]

  const waitForMapCaptures = () =>
    new Promise(resolve => {
      const esgReady = () => detailStore.esgMapsReady
      const planningReady = () => detailStore.planningMapReady
      let planningWaived = false
      const allReady = () => esgReady() && (planningReady() || planningWaived)

      if (allReady()) {
        resolve(true)
        return
      }

      detailStore.exportPhase = 'waiting-maps'
      const missing = () =>
        detailStore.enabledEsgMapKeys.filter(
          key => !detailStore.capturedEsgMapKeys.includes(key),
        )
      const nudge = () => {
        // Kick failed/blank captures — covers the '' error sentinel
        // EsgItemComponent writes when a capture throws, and a plannings
        // capture that is behind its overlay (never while the planning
        // payload is still in flight: there is nothing to capture yet).
        if (!esgReady()) {
          detailStore.requestEsgRecapture(missing())
        }
        if (!planningReady() && !detailStore.planningInfoPending) {
          detailStore.requestPlanningRecapture()
        }
      }
      nudge()

      let stopWatch = null
      let timers = []
      const finish = ok => {
        stopWatch?.()
        timers.forEach(clearTimeout)
        resolve(ok)
      }
      stopWatch = watch(allReady, ready => ready && finish(true))
      timers = [
        ...RETRY_AT_MS.map(ms => setTimeout(nudge, ms)),
        setTimeout(() => {
          if (!planningReady()) {
            console.warn(
              '[useExportDetail] plannings map capture still behind its overlay — exporting what was captured',
            )
          }
          planningWaived = true
          if (allReady()) {
            finish(true)
          }
        }, PLANNING_WAIT_TIMEOUT_MS),
        setTimeout(() => finish(esgReady()), ESG_WAIT_TIMEOUT_MS),
      ]
    })

  // 'done' flashes on the subheader bar for a moment before returning to
  // idle; a new export cancels the pending flash so phases never fight.
  let donePhaseTimer = null
  const flashDonePhase = () => {
    detailStore.exportPhase = 'done'
    clearTimeout(donePhaseTimer)
    donePhaseTimer = setTimeout(() => {
      if (detailStore.exportPhase === 'done') {
        detailStore.exportPhase = 'idle'
      }
    }, 1500)
  }

  const executeExport = async () => {
    detailStore.isExporting = true
    clearTimeout(donePhaseTimer)
    detailStore.exportUploadProgress = 0

    internalExportForm.value.evaluationId = evaluationStore.id
    internalExportForm.value.templateId = detailStore.evaluationTemplateId
    internalExportForm.value.mainForm = detailStore.mainForm
      ? { ...detailStore.mainForm }
      : {}

    // Validation
    if (
      !internalExportForm.value.mainForm.eksperthinnangu_nr?.length ||
      !internalExportForm.value.templateId
    ) {
      toast.add({
        severity: 'error',
        summary: t('messages.detail.error'),
        detail: t('messages.detail.errors.template_or_doc_no_not_filled'),
      })
      detailStore.isExporting = false
      return
    }

    // Wait for the offscreen ESG captures and the plannings capture BEFORE
    // assembling the payload, so the map payload below always reads a fresh
    // detailStore.mapExport. Auto-retries stragglers; only a full ESG
    // timeout surfaces the warning.
    if (!(await waitForMapCaptures())) {
      toast.add({
        severity: 'warn',
        summary: t('messages.detail.error'),
        detail: t('messages.detail.errors.esg_maps_not_loaded'),
        life: 8000,
      })
      detailStore.exportPhase = 'idle'
      detailStore.isExporting = false
      return
    }

    if (internalExportForm.value.mainForm.vaartuse_kuupaev) {
      internalExportForm.value.mainForm.vaartuse_kuupaev = formatDatePickerToISO(internalExportForm.value.mainForm.vaartuse_kuupaev)
    }

    if (internalExportForm.value.mainForm.ylevaatuse_kuupaev) {
      internalExportForm.value.mainForm.ylevaatuse_kuupaev = formatDatePickerToISO(internalExportForm.value.mainForm.ylevaatuse_kuupaev)
    }

    if (internalExportForm.value.mainForm.aruande_kuupaev) {
      internalExportForm.value.mainForm.aruande_kuupaev = formatDatePickerToISO(internalExportForm.value.mainForm.aruande_kuupaev)
    }

    // Data aggregation (directly assigning to internalExportForm.value)
    internalExportForm.value.approverId = detailStore.evaluationApproverId
    internalExportForm.value.ehrGeneral = detailStore.ehrGeneral
    internalExportForm.value.ehrTechnical = detailStore.ehrTechnical
    internalExportForm.value.ehrConstruction = detailStore.ehrConstruction
    internalExportForm.value.ehrCommunal = detailStore.ehrCommunal
    internalExportForm.value.interior = detailStore.interior
    internalExportForm.value.infra = infraStore.infra
    internalExportForm.value.landBoard = detailStore.landBoard

    if (evaluationStore.objectType === 'ER') {
      internalExportForm.value.evaluationLivingRoom =
        evaluationStore.selectedBuildingPart
    } else {
      internalExportForm.value.evaluationNotLivingRoom =
        evaluationStore.selectedBuildingPart
    }

    internalExportForm.value.documents = detailStore.getSelectedDocuments
    // RED-293: üldplaneering facts + juhtotstarve + legend for the docx
    // Planeeringud section (the map excerpt travels via map.plannings). The
    // captured viewport lets the backend compose a legend of exactly the
    // categories visible in the exported excerpt; legendUrl is only the
    // full-legend fallback.
    internalExportForm.value.planningInfo = detailStore.planningInfo?.found
      ? {
          plan: detailStore.planningInfo.plan,
          landUses: detailStore.planningInfo.landUses,
          landUseSource: detailStore.planningInfo.landUseSource,
          legendUrl: detailStore.planningInfo.legendUrl,
          mapView: detailStore.planningMapView,
        }
      : null
    internalExportForm.value.restrictions = detailStore.restrictions
    internalExportForm.value.realEstateGeneral = detailStore.realEstateGeneral
    internalExportForm.value.realEstatePart1Main =
      detailStore.realEstatePart1Main
    internalExportForm.value.realEstatePart1Entries =
      detailStore.realEstatePart1Entries
    internalExportForm.value.realEstatePart2 = detailStore.realEstatePart2
    internalExportForm.value.realEstatePart3 = detailStore.realEstatePart3
    internalExportForm.value.realEstatePart4 = detailStore.realEstatePart4
    internalExportForm.value.buildings = detailStore.selectedBuildings

    // RED-296: self-heal a stale comparison config first (the deferred EHR
    // building part may have landed while ComparisonTable was unmounted), so
    // maatüki rows can never leak into the docx of an apartment evaluation.
    transactionStore.initComparisonTableConfig()

    internalExportForm.value.transactions = {
      selection: transactionStore.selectionTableExport,
      comparison: transactionStore.comparisonTableExport,
      // RED-296: hiddenKeys lets the backend drop user-hidden rows defensively;
      // comparison rows above already arrive pre-filtered.
      table_conf: {
        ...transactionStore.markedTableConf,
        hiddenKeys: [...transactionStore.hiddenComparisonKeys],
      },
      selectedPriceBase: transactionStore.selectedPriceBase,
    }

    internalExportForm.value.esg = Object.keys(detailStore.esgData).reduce(
      (acc, key) => {
        if (detailStore.esgData[key] && detailStore.esgData[key].user) {
          acc[key] = {
            description: detailStore.esgData[key].user.description,
            evaluation: detailStore.esgData[key].user.evaluation,
          }
        } else {
          // Handle cases where esgData[key] or user might be undefined
          acc[key] = { description: '', evaluation: '' }
        }
        return acc
      },
      {},
    )
    internalExportForm.value.esgSummary = detailStore.esgSummary

    internalExportForm.value.map = {
      micro: '',
      macro: '',
      restriction: '',
      landBoard: '',
      noise_level: '',
      traffic_volume: '',
      mineral_deposit: '',
      hazardous_installations: '',
      residual_pollution: '',
      radon_risk: '',
      flood_risk: '',
      heat_islands: '',
      infra: '',
      plannings: '',
    }
    const mapsFromStore = detailStore.mapExport
    if (mapsFromStore) {
      // Check if mapsFromStore is not null/undefined
      Object.keys(internalExportForm.value.map).forEach(key => {
        if (key in mapsFromStore) {
          internalExportForm.value.map[key] = mapsFromStore[key]
        }
      })
    }

    Object.keys(DETAIL_ESG).forEach(key => {
      if (
        internalExportForm.value.esg &&
        key in internalExportForm.value.esg &&
        DETAIL_ESG[key]
      ) {
        internalExportForm.value.esg[key].legends = DETAIL_ESG[key].legends
      }
    })

    Object.keys(esgStore.legends).forEach(key => {
      if (key in internalExportForm.value.esg) {
        const existing = new Set(internalExportForm.value.esg[key].legends || [])
        const unique = esgStore.legends[key].filter(url => !existing.has(url))
        internalExportForm.value.esg[key].legends?.push(...unique)
      }
    })

    detailStore.exportPhase = 'uploading'

    axios
      .post('/export', internalExportForm.value, {
        responseType: 'blob',
        // The payload carries ~12 data-URL map captures — upload time is
        // real. Flip to indeterminate 'processing' as soon as the last byte
        // is out; the gap until the response is genuine server time.
        onUploadProgress: event => {
          const total = event.total || event.loaded
          const pct = total ? Math.round((event.loaded / total) * 100) : 0
          detailStore.exportUploadProgress = pct
          if (pct >= 100 && detailStore.exportPhase === 'uploading') {
            detailStore.exportPhase = 'processing'
          }
        },
      })
      .then(async response => {
        // RED-255 async export: behind the async-export flag the server queues
        // the render and answers 202 JSON instead of streaming the blob. The
        // button spinner is the feedback; usePoll watches the activeExport
        // page prop until the render lands.
        const contentType = response.headers?.['content-type'] || ''
        if (contentType.includes('json')) {
          const { exportId } = JSON.parse(await response.data.text())
          trackExport(exportId)
          return
        }

        flashDonePhase()
        downloadBlob(response.data)
        detailStore.isExporting = false
      })
      .catch(async error => {
        detailStore.exportPhase = 'idle'
        detailStore.isExporting = false

        // Network-level failures (connection reset, timeout, aborted stream)
        // reject with no response object at all — this used to TypeError
        // inside the handler and fail with no message (the RED-255 silent
        // export bug). Always surface something.
        const { request, response } = error || {}
        if (!response) {
          toast.add({
            severity: 'error',
            summary: t('messages.detail.error'),
            detail: t('messages.detail.errors.export_network_failed'),
          })
          return
        }

        let data = response.data

        if (
          request?.responseType === 'blob' &&
          data instanceof Blob &&
          data.type
        ) {
          const text = await data.text()

          data = data.type.toLowerCase().includes('json')
            ? JSON.parse(text)
            : text
        }

        if (data.action === 'reload') {
          router.reload({
            only: data.props,
            preserveState: true,
            preserveScroll: true,
            async: true,
          })
        }

        toast.add({
          severity: 'error',
          summary: t('messages.detail.error'),
          detail: data.message || t('messages.detail.errors.export_failed'),
        })
      })
  }

  // Legacy sync path (async-export flag off): the whole docx arrived as a
  // blob — hand it to the browser under the frontend-built filename.
  const downloadBlob = blobData => {
    let optionalFilenameData = ''
    if (
      internalExportForm.value.mainForm &&
      internalExportForm.value.mainForm.vara &&
      internalExportForm.value.mainForm.vara !== ''
    ) {
      optionalFilenameData = '(' + internalExportForm.value.mainForm.vara + ')'
    }
    const filename =
      (internalExportForm.value.mainForm?.eksperthinnangu_nr || 'export') +
      '-' +
      (internalExportForm.value.mainForm?.aadress || 'data') +
      optionalFilenameData +
      '.docx'

    const url = window.URL.createObjectURL(new Blob([blobData]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  // ── RED-255 async export tracking ─────────────────────────────────
  // The queued render's state rides the Detail page's `activeExport` prop;
  // usePoll partial-reloads just that prop until the render lands. Because
  // the state lives server-side, a page refresh mid-render resumes cleanly.

  const EXPORT_TIMEOUT_MS = 5 * 60 * 1000

  const page = usePage()
  const trackedExportId = ref(null)
  let exportDeadline = 0

  const { start: startExportPoll, stop: stopExportPoll } = usePoll(
    2000,
    { only: ['activeExport'], async: true },
    { autoStart: false, keepAlive: true, mode: 'rest' },
  )

  const trackExport = exportId => {
    trackedExportId.value = exportId
    exportDeadline = Date.now() + EXPORT_TIMEOUT_MS
    startExportPoll()
  }

  const finishExportTracking = () => {
    trackedExportId.value = null
    stopExportPoll()
    detailStore.isExporting = false
  }

  watch(
    () => page.props.activeExport,
    activeExport => {
      if (
        trackedExportId.value === null ||
        activeExport?.id !== trackedExportId.value
      ) {
        return
      }

      if (activeExport.status === 'completed') {
        finishExportTracking()
        flashDonePhase()
        // Browser-native download: no blob buffering, server-set filename.
        const link = document.createElement('a')
        link.href = `/export/${activeExport.id}/download`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return
      }

      if (activeExport.status === 'failed') {
        finishExportTracking()
        detailStore.exportPhase = 'idle'
        toast.add({
          severity: 'error',
          summary: t('messages.detail.error'),
          detail:
            activeExport.error_message ||
            t('messages.detail.errors.export_failed'),
        })
        return
      }

      if (Date.now() > exportDeadline) {
        finishExportTracking()
        detailStore.exportPhase = 'idle'
        toast.add({
          severity: 'error',
          summary: t('messages.detail.error'),
          detail: t('messages.detail.errors.export_timeout'),
        })
      }
    },
  )

  // A render queued before a refresh/navigation is still in flight — pick it
  // back up so the finished file downloads instead of quietly stranding.
  const initialExport = page.props.activeExport
  if (
    initialExport &&
    ['queued', 'processing'].includes(initialExport.status)
  ) {
    detailStore.isExporting = true
    detailStore.exportPhase = 'processing'
    trackExport(initialExport.id)
  }

  return {
    executeExport,
  }
}
